import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Button } from '@artstorefronts/ui';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { adminSidebarClass } from './admin-nav';

const HOME_HREF = '/admin/home';

/**
 * Every admin section. Order here is irrelevant — the sidebar pins Home to the top and sorts the
 * rest by their translated label, so a new section only has to be added to this list.
 */
const NAV_KEYS: Array<{ key: string; href: string }> = [
  { key: 'home', href: HOME_HREF },
  { key: 'acquisition', href: '/admin/acquisition' },
  { key: 'artist-genome', href: '/admin/artist-genome' },
  { key: 'artist-spotlight', href: '/admin/artist-spotlight' },
  { key: 'badges', href: '/admin/badges' },
  { key: 'chats', href: '/admin/chats' },
  { key: 'community', href: '/admin/community' },
  { key: 'community-invites', href: '/admin/community-invites' },
  { key: 'contests', href: '/admin/contests' },
  { key: 'feature-wizards', href: '/admin/feature-wizards' },
  { key: 'features', href: '/admin/features' },
  { key: 'feed-cards', href: '/admin/feed-cards' },
  { key: 'feed-strategies', href: '/admin/feed-strategies' },
  { key: 'pre-feeds', href: '/admin/pre-feeds' },
  { key: 'quests', href: '/admin/quests' },
  { key: 'bounties', href: '/admin/bounties' },
  { key: 'referrals', href: '/admin/referrals' },
  { key: 'dm-translation', href: '/admin/dm-translation' },
  { key: 'geographic-centrality', href: '/admin/geographic-centrality' },
  { key: 'import-artists', href: '/admin/import-artists' },
  { key: 'marketing-links', href: '/admin/marketing-links' },
  { key: 'metrics', href: '/admin/metrics' },
  { key: 'mocks', href: '/admin/mocks' },
  { key: 'nav-config', href: '/admin/nav-config' },
  { key: 'moderation', href: '/admin/moderation' },
  { key: 'onboarding', href: '/admin/onboarding' },
  { key: 'profiles', href: '/admin/profiles' },
  { key: 'sparklinks', href: '/admin/sparklinks' },
  { key: 'sparks', href: '/admin/sparks' },
  { key: 'users', href: '/admin/users' },
  { key: 'points', href: '/admin/points' },
  { key: 'rewards', href: '/admin/rewards' },
  { key: 'verification-requests', href: '/admin/verification-requests' },
];

/**
 * Exact match, or a child route beneath it. A bare `startsWith` would light up Community while the
 * reader is on Community Invites, since one href is a prefix of the other.
 */
function isCurrent(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function AdminSidebar({ open }: { open: boolean }) {
  const { t } = useTranslation('admin');
  const location = useLocation();

  const items = NAV_KEYS.map((item) => {
    return { ...item, name: t(`nav.${item.key}`) };
  });
  const home = items.filter((item) => item.href === HOME_HREF);
  const rest = items.filter((item) => item.href !== HOME_HREF).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <aside id="admin-nav" className={adminSidebarClass(open)}>
      <nav className="flex flex-col gap-0.5 p-3">
        {[...home, ...rest].map((item) => {
          const current = isCurrent(location.pathname, item.href);

          return (
            <Link
              key={item.href}
              to={item.href}
              aria-current={current ? 'page' : undefined}
              className={`rounded-md px-3 py-2 text-sm tracking-tight ${
                current
                  ? 'bg-zinc-100 font-bold text-black'
                  : 'font-normal text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              {item.name}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}

function Admin() {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="sticky top-0 z-50 w-full border-b border-zinc-200 bg-white">
        <div className="px-4 md:px-8">
          <div className="flex h-16 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-700 hover:bg-zinc-100 md:hidden"
                aria-expanded={navOpen}
                aria-controls="admin-nav"
                onClick={() => setNavOpen((open) => !open)}
              >
                <Menu className="h-4 w-4" />
                <span className="sr-only">{navOpen ? 'Close admin menu' : 'Open admin menu'}</span>
              </button>
              <Link to="/admin" className="flex items-center">
                <img src="/arthelperai-logo-black-horizontal.png" alt="ArtHelper" className="h-8" />
              </Link>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                {t('nav.back-to-arthelper')}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {navOpen ? (
          <button
            type="button"
            className="fixed inset-0 top-16 z-30 bg-black/40 md:hidden"
            aria-label="Close admin menu"
            onClick={() => setNavOpen(false)}
          />
        ) : null}
        <AdminSidebar open={navOpen} />
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Admin;
